/**
 * Tool schema helpers for Inky: Gemini function declarations generated from the tool
 * registry, name mapping (registry names use dots, Gemini ids use underscores), light
 * argument validation that fails with self-correcting messages, and compact result capping.
 *
 * Pure module (no React, no network) so it can be unit tested in isolation.
 */

/** Max characters of a tool result handed back to the model in one functionResponse. */
export const TOOL_RESULT_MAX_CHARS = 8000;
/** Max characters of a tool result persisted with the message (restored next turn). */
export const TOOL_RESULT_PERSIST_CHARS = 2000;

/** JSON-schema keys Gemini's OpenAPI subset accepts on a parameter schema. */
const ALLOWED_SCHEMA_KEYS = new Set([
  "type",
  "description",
  "enum",
  "items",
  "properties",
  "required",
  "nullable",
  "format",
  "minimum",
  "maximum",
  "minItems",
  "maxItems",
]);

/**
 * Convert a registry tool name (e.g. `doc.edit`) to a Gemini-safe function name (`doc_edit`).
 * @param {string} name - Registry tool name.
 * @returns {string} Gemini function name.
 */
export const toGeminiName = (name) =>
  String(name || "")
    .replace(/[^a-zA-Z0-9_]/g, "_")
    .slice(0, 64);

/**
 * Strip a JSON schema down to the keys Gemini accepts (recursively).
 * @param {object} schema - JSON schema fragment.
 * @returns {object} Sanitized schema.
 */
export const sanitizeSchema = (schema) => {
  if (!schema || typeof schema !== "object" || Array.isArray(schema)) {
    return { type: "string" };
  }
  const out = {};
  for (const [key, value] of Object.entries(schema)) {
    if (!ALLOWED_SCHEMA_KEYS.has(key)) continue;
    if (key === "properties" && value && typeof value === "object") {
      out.properties = {};
      for (const [prop, propSchema] of Object.entries(value)) {
        out.properties[prop] = sanitizeSchema(propSchema);
      }
    } else if (key === "items") {
      out.items = sanitizeSchema(value);
    } else {
      out[key] = value;
    }
  }
  if (!out.type) out.type = out.properties ? "object" : "string";
  if (out.type === "object" && !out.properties) out.properties = {};
  if (Array.isArray(out.required) && out.properties) {
    out.required = out.required.filter((key) => key in out.properties);
    if (!out.required.length) delete out.required;
  }
  return out;
};

/**
 * Whether a tool is offered in the current context.
 * @param {object} tool - Registry tool.
 * @param {object} ctx - Tool availability context.
 * @returns {boolean} True when the tool may be offered to the model.
 */
export const isToolAvailable = (tool, ctx) => {
  if (!tool || typeof tool.run !== "function") return false;
  if (typeof tool.available !== "function") return true;
  try {
    return tool.available(ctx) !== false;
  } catch {
    return false;
  }
};

/**
 * Build Gemini `functionDeclarations` from the registry (only tools available now).
 * @param {Array<object>} tools - Tool registry.
 * @param {object} ctx - Availability context passed to `tool.available`.
 * @returns {Array<{name: string, description: string, parameters: object}>} Declarations.
 */
export const buildFunctionDeclarations = (tools, ctx = {}) =>
  (tools || [])
    .filter((tool) => isToolAvailable(tool, ctx))
    .map((tool) => {
      const declaration = {
        name: toGeminiName(tool.name),
        description: String(tool.description || "").slice(0, 1000),
      };
      const params = sanitizeSchema(tool.parameters || { type: "object", properties: {} });
      if (params.properties && Object.keys(params.properties).length) {
        declaration.parameters = params;
      }
      return declaration;
    });

/**
 * Find a registry tool by its registry name or its Gemini function name.
 * @param {Array<object>} tools - Tool registry.
 * @param {string} name - Name reported by the model.
 * @returns {object | null} Matching tool.
 */
export const findTool = (tools, name) => {
  if (!name) return null;
  const wanted = String(name);
  return (
    (tools || []).find((tool) => tool.name === wanted) ||
    (tools || []).find((tool) => toGeminiName(tool.name) === wanted) ||
    null
  );
};

/**
 * Coerce one argument to the declared type when the model sent a near miss (e.g. "3" for a number).
 * @param {*} value - Raw value.
 * @param {object} schema - Property schema.
 * @returns {*} Coerced value.
 */
const coerceValue = (value, schema) => {
  if (value == null || !schema) return value;
  switch (schema.type) {
    case "number":
    case "integer": {
      if (typeof value === "number") return value;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : value;
    }
    case "boolean":
      if (typeof value === "boolean") return value;
      if (value === "true") return true;
      if (value === "false") return false;
      return value;
    case "array":
      if (Array.isArray(value)) return value;
      if (typeof value === "string" && value.trim()) return [value];
      return value;
    case "string":
      if (typeof value === "string") return value;
      if (typeof value === "number" || typeof value === "boolean") return String(value);
      return value;
    default:
      return value;
  }
};

/**
 * Validate (and lightly coerce) model-supplied args against a tool's JSON schema. Errors
 * name the valid keys/options so the model can correct itself within the same turn.
 * @param {object} schema - Tool parameter schema (type object).
 * @param {object} args - Raw arguments from the model.
 * @returns {{ok: boolean, value: object, errors: string[]}} Validation result.
 */
export const validateArgs = (schema, args) => {
  const properties = schema?.properties || {};
  const required = Array.isArray(schema?.required) ? schema.required : [];
  const input = args && typeof args === "object" && !Array.isArray(args) ? args : {};
  const value = {};
  const errors = [];

  for (const [key, raw] of Object.entries(input)) {
    if (!(key in properties)) {
      // Unknown keys are dropped silently: models often add harmless extras.
      continue;
    }
    const propSchema = properties[key];
    const coerced = coerceValue(raw, propSchema);
    if (Array.isArray(propSchema.enum) && coerced != null && coerced !== "") {
      if (!propSchema.enum.includes(coerced)) {
        errors.push(
          `"${key}" must be one of: ${propSchema.enum.map((option) => JSON.stringify(option)).join(", ")}.`
        );
        continue;
      }
    }
    value[key] = coerced;
  }

  for (const key of required) {
    const present = value[key];
    if (present == null || (typeof present === "string" && !present.trim())) {
      errors.push(`Missing required argument "${key}".`);
    }
  }

  if (errors.length && Object.keys(properties).length) {
    errors.push(`Valid arguments: ${Object.keys(properties).join(", ")}.`);
  }

  return { ok: errors.length === 0, value, errors };
};

/**
 * Serialize a tool result for the model, capped to `maxChars`.
 * @param {*} data - Tool result data.
 * @param {number} [maxChars] - Character cap.
 * @returns {string} Capped serialization.
 */
export const capResultText = (data, maxChars = TOOL_RESULT_MAX_CHARS) => {
  let text;
  if (typeof data === "string") {
    text = data;
  } else {
    try {
      text = JSON.stringify(data);
    } catch {
      text = String(data);
    }
  }
  if (text == null) return "";
  if (text.length <= maxChars) return text;
  return `${text.slice(0, maxChars)}\n...(truncated ${text.length - maxChars} characters)`;
};

/**
 * Build the object placed in a Gemini functionResponse part (`response` must be an object).
 * @param {*} data - Tool result data.
 * @param {number} [maxChars] - Character cap for large payloads.
 * @returns {object} Response object.
 */
export const toFunctionResponsePayload = (data, maxChars = TOOL_RESULT_MAX_CHARS) => {
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const text = capResultText(data, maxChars);
    if (text.length <= maxChars) return data;
    return { result: text };
  }
  return { result: capResultText(data, maxChars) };
};

/**
 * Stable signature of a call for the anti-repeat guard.
 * @param {string} name - Tool name.
 * @param {object} args - Arguments.
 * @returns {string} Signature.
 */
export const callSignature = (name, args) => {
  const sortKeys = (input) => {
    if (Array.isArray(input)) return input.map(sortKeys);
    if (input && typeof input === "object") {
      return Object.keys(input)
        .sort()
        .reduce((acc, key) => {
          acc[key] = sortKeys(input[key]);
          return acc;
        }, {});
    }
    return input;
  };
  return `${name}:${JSON.stringify(sortKeys(args || {}))}`;
};

/**
 * Compact tool list for the legacy text-JSON protocol prompt (names + arg type hints).
 * @param {Array<object>} tools - Registry tools (already filtered for availability).
 * @returns {Array<{name: string, description: string, parameters: Record<string, string>}>} Compact specs.
 */
export const compactToolsForPrompt = (tools) =>
  (tools || []).map((tool) => {
    const properties = tool.parameters?.properties || {};
    const required = new Set(tool.parameters?.required || []);
    const parameters = {};
    for (const [key, schema] of Object.entries(properties)) {
      const enumHint = Array.isArray(schema.enum) ? ` (${schema.enum.join("|")})` : "";
      parameters[key] = `${schema.type || "string"}${enumHint}${required.has(key) ? ", required" : ""}`;
    }
    return { name: tool.name, description: tool.description, parameters };
  });
